import type {
  JournalPrefillLine,
  JournalPrefillSourceRef,
} from "@care-by-ella/contracts";

// TES-978 — "För varje förifylld uppgift ska Kirill kunna se källa, datum, bakomliggande fråga och patientens originalsvar."
// One line of a box's "Källa": where it came from and when, the question as
// she was asked it and her answer as she gave it. Null for a line that only
// structures the box (a blank between groups).
export function sourceDescription(line: JournalPrefillLine): string | null {
  return line.source ? describe(line.source) : null;
}

// TES-984 — a compact line that sums up several answers (the same question
// over several weeks) lists every one of them, oldest first, so each
// original answer and its date stays reachable.
export function sourceDescriptions(line: JournalPrefillLine): string[] {
  const refs = line.sources?.length
    ? line.sources
    : line.source
      ? [line.source]
      : [];
  return refs.map(describe);
}

// TES-1019 (Emelie, point 1) — a form source recorded since TES-1019 carries
// the version she answered; it is shown with the submission time (Swedish
// time), so the doctor can tell which wording she saw and when. An older
// source has no version and reads as before, with the date only.
function submittedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d
    .toLocaleString("sv-SE", {
      timeZone: "Europe/Stockholm",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", "");
}

// The form (or reading, or order), its version and when: "Inledande frågor
// version 9 2026-08-01 14:05".
function origin(s: JournalPrefillSourceRef): string {
  const versioned = s.templateVersion != null;
  return [
    s.label,
    versioned ? `version ${s.templateVersion}` : null,
    s.date ? (versioned ? submittedAt(s.date) : s.date.slice(0, 10)) : null,
  ]
    .filter(Boolean)
    .join(" ");
}

function describe(s: JournalPrefillSourceRef): string {
  const parts = [origin(s)];
  if (s.question) parts.push(`Fråga: ${s.question}`);
  if (s.answer) parts.push(`Svar: ${s.answer}`);
  return parts.join(" · ");
}

// TES-1244 — the review's "källa och datum": each form, reading or order the
// new material came from, once, in the order its lines name them.
export function sourceOrigins(lines: readonly JournalPrefillLine[]): string[] {
  const refs = lines.flatMap((l) =>
    l.sources?.length ? l.sources : l.source ? [l.source] : [],
  );
  return [...new Set(refs.map(origin))];
}

// New material goes under what the doctor already wrote, never in place of
// it. Since TES-1244 this is only the INPUT of the proposal ("compose"),
// never written into the box as it is.
export function appendNewSource(current: string, incoming: string): string {
  const base = current.replace(/\s+$/, "");
  const add = incoming.trim();
  if (!add) return current;
  return base ? `${base}\n\n${add}` : add;
}

// ---- TES-1244 (TES-1240) "Granska kompletteringen" ---------------------------
//
// Emelie (TES-1240): "”Nytt underlag finns” och ”Infoga”. Dessa formuleringar
// ska ersättas med: Begärd komplettering har kommit in → Granska
// kompletteringen. eller Ny information har kommit in → Granska informationen."
// "Råa frågor och svar får aldrig klistras in i journalen. Om Kirill har
// redigerat fältet ska han få en jämförelse och valen: Godkänn och uppdatera
// [journalrubrik]. Behåll nuvarande journaltext."

export function newSourceCopy(requested: boolean | undefined): {
  title: string;
  action: string;
} {
  return requested
    ? {
        title: "Begärd komplettering har kommit in",
        action: "Granska kompletteringen",
      }
    : {
        title: "Ny information har kommit in",
        action: "Granska informationen",
      };
}

export function approveNewSourceLabel(heading: string): string {
  return `Godkänn och uppdatera ${heading}`;
}

export const KEEP_JOURNAL_TEXT = "Behåll nuvarande journaltext";

/**
 * How the proposed text is asked for, through the same per-heading AI as the
 * compile's summary (TES-1312). A box nobody has written in (or that was
 * cleared) is proposed as the summary of the new prefill, which replaces the
 * old one; a box the doctor wrote in is proposed as his text and the new
 * material written together, so nothing he wrote is dropped.
 */
export function newSourceProposalRequest(box: {
  current: string;
  /** Whether a clinician has saved this box (the section's `editedAt`). */
  edited: boolean;
  /** The new prefill's text. */
  incoming: string;
}): { mode: "summary" } | { mode: "compose"; text: string } {
  if (!box.edited || box.current.trim() === "") return { mode: "summary" };
  return { mode: "compose", text: appendNewSource(box.current, box.incoming) };
}

/**
 * The text the review may propose, or null. Only the model's own text: the
 * fallback is the deterministic prefill (her raw answers) or the box with
 * them appended, and raw answers are never proposed for the journal.
 */
export function usableProposal(result: {
  text: string;
  source: "ai" | "fallback";
}): string | null {
  return result.source === "ai" && result.text.trim() ? result.text : null;
}
